import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import Auth from "@aws-amplify/auth";
import { NbToastrService } from "@nebular/theme";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ROUTER_NAVIGATED } from "@ngrx/router-store";
import { Store } from "@ngrx/store";
import { forkJoin, of, throwError, timer } from "rxjs";
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { environment } from "../environments/environment";
import {
  addTagToApp,
  addTagToAppSuccess,
  assignTags,
  chooseContract,
  createApp,
  createAppSuccess,
  createQueueItem,
  createTag,
  createTagsSuccess,
  deleteApp,
  deleteTag,
  deleteTagFromApp,
  deleteTagFromAppSuccess,
  fetchApps,
  fetchAppsSuccess,
  fetchContract,
  fetchContractSuccess,
  fetchCustomer,
  fetchCustomerSuccess,
  fetchHistory,
  fetchHistorySuccess,
  fetchQueueStats,
  fetchQueueStatsSuccess,
  fetchTags,
  fetchTagsSuccess,
  logout,
  logoutSuccess,
  sendTestPush,
  sendTestPushSuccess,
  updateApp,
  updateAppSuccess,
  updateTag,
  updateTagSuccess,
} from "./app.actions";
import { App, AppState, HistoryStatus, Tag } from "./app.models";
import {
  selectApiEndpoint,
  selectBaseEndpoint,
  selectClientId,
  selectContract,
  selectCustomerApiEndpoint,
  selectRouteParam,
  selectRouteParams,
  selectTestPushCredentials,
  selectUrl,
} from "./app.selector";

@Injectable()
export class AppEffects {
  routerNavigated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      withLatestFrom(this.store$.select(selectRouteParam("clientId"))),
      filter(([action, clientId]) => !!clientId),
      map(([action, clientId]) => ({
        type: chooseContract.type,
        payload: clientId,
      }))
    )
  );

  fetchContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchContract),
      switchMap(() =>
        this.http.get(`${environment.awsApiGateway}/customer-information`)
      ),
      catchError(
        (err) =>
          this.toastr.warning(`Failed to fetch contract info.`) &&
          throwError(err)
      ),
      map((resp: any) => {
        if (!resp.contracts) {
          resp.contracts = [];
        }
        return { type: fetchContractSuccess.type, payload: resp };
      })
    )
  );

  fetchContractsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchContractSuccess),
        withLatestFrom(
          this.store$.select(selectRouteParam("clientId")),
          this.store$.select(selectUrl)
        ),
        tap(([action, clientId, url]) => {
          if (!clientId && action.payload.contracts[0]) {
            const firstClientId = action.payload.contracts[0].clientId;
            this.router.navigate([
              `${firstClientId}${url !== "/" ? url : "/home"}`,
            ]);
          } else if (!clientId && !action.payload.contracts[0]) {
            this.router.navigate(["/home"]);
          }
        })
      ),
    { dispatch: false }
  );

  contractAndClientIdAvailable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchContractSuccess),
      switchMap(() => this.store$.select(selectContract)),
      filter((clientId) => !!clientId),
      tap((e) => {
        // console.log(e);
      }),
      switchMap(() => [
        { type: fetchCustomer.type },
        { type: fetchTags.type },
        { type: fetchApps.type },
        // { type: fetchHistory.type },
      ])
    )
  );

  fetchCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCustomer),
      withLatestFrom(
        this.store$.select(selectContract),
        this.store$.select(selectApiEndpoint)
      ),
      switchMap(([action, contract, endpoint]) =>
        this.http.get(`${endpoint}/${contract.clientId}/customer`)
      ),
      catchError(
        (err) =>
          this.toastr.warning(`Failed to fetch customer.`) && throwError(err)
      ),
      map((resp) => ({ type: fetchCustomerSuccess.type, payload: resp }))
    )
  );

  fetchApps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchApps),
      withLatestFrom(
        this.store$.select(selectContract),
        this.store$.select(selectApiEndpoint)
      ),
      switchMap(([action, contract, endpoint]) =>
        this.http.get(`${endpoint}/${contract.clientId}/apps`)
      ),
      catchError(
        (err) =>
          this.toastr.warning(`Failed to fetch app list.`) && throwError(err)
      ),
      map((resp) => ({ type: fetchAppsSuccess.type, payload: resp }))
    )
  );

  createApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createApp),
      withLatestFrom(
        this.store$.select(selectApiEndpoint),
        this.store$.select(selectRouteParams)
      ),
      switchMap(([action, endpoint, params]) =>
        this.http.post(
          `${endpoint}/${params.clientId}/apps`,
          this.appFormData(action)
        )
      ),
      catchError(
        (err) => this.toastr.warning(`Failed to create app.`) && throwError(err)
      ),
      tap(() => this.toastr.success("App created successfully.")),
      map((resp: App) => ({
        type: createAppSuccess.type,
        payload: { app: resp },
      }))
    )
  );

  updateApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateApp),
      withLatestFrom(
        this.store$.select(selectApiEndpoint),
        this.store$.select(selectUrl)
      ),
      switchMap(([action, endpoint, url]) =>
        this.http.put(endpoint + url, this.appFormData(action))
      ),
      catchError(
        (err) => this.toastr.warning(`Failed to update app.`) && throwError(err)
      ),
      tap(() => this.toastr.success("App updated successfully.")),
      map((resp: App) => ({
        type: updateAppSuccess.type,
        payload: { app: resp },
      }))
    )
  );

  deleteApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteApp),
      withLatestFrom(
        this.store$.select(selectApiEndpoint),
        this.store$.select(selectClientId)
      ),
      switchMap(([action, endpoint, clientId]) => {
        return this.http.delete(
          `${endpoint}/${clientId}/apps/${action.payload.id}`
        );
      }),
      catchError(
        (err) => this.toastr.warning("Failed to delete app.") && throwError(err)
      ),
      tap(() => this.toastr.success("App deleted successfully.")),
      map((resp) => ({ type: fetchApps.type }))
    )
  );

  assignTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignTags),
      switchMap((action) =>
        action.payload.changes.map((c) => {
          if (c.action === "add") {
            return {
              type: addTagToApp.type,
              payload: { appid: action.payload.app.id, tagid: c.tagid },
            };
          } else if (c.action === "delete") {
            return {
              type: deleteTagFromApp.type,
              payload: { appid: action.payload.app.id, tagid: c.tagid },
            };
          }
        })
      )
    )
  );

  addTagToApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addTagToApp),
      withLatestFrom(
        this.store$.select(selectClientId),
        this.store$.select(selectApiEndpoint)
      ),
      concatMap(([action, clientId, endpoint]) =>
        this.http.post(
          `${endpoint}/${clientId}/apps/${action.payload.appid}/tags/${action.payload.tagid}`,
          {}
        )
      ),
      map((resp) => ({ type: addTagToAppSuccess.type, payload: { app: resp } }))
    )
  );

  deleteTagFromApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTagFromApp),
      withLatestFrom(
        this.store$.select(selectClientId),
        this.store$.select(selectApiEndpoint)
      ),
      concatMap(([action, clientId, endpoint]) =>
        this.http.delete(
          `${endpoint}/${clientId}/apps/${action.payload.appid}/tags/${action.payload.tagid}`
        )
      ),
      map((resp) => ({
        type: deleteTagFromAppSuccess.type,
        payload: { app: resp },
      }))
    )
  );

  fetchTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchTags),
      withLatestFrom(
        this.store$.select(selectContract),
        this.store$.select(selectApiEndpoint)
      ),
      switchMap(([action, contract, endpoint]) =>
        this.http.get(`${endpoint}/${contract.clientId}/tags`)
      ),
      catchError(
        (err) =>
          this.toastr.warning(`Failed to fetch tag list.`) && throwError(err)
      ),
      map((resp) => ({ type: fetchTagsSuccess.type, payload: resp }))
    )
  );

  createTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createTag),
      withLatestFrom(this.store$.select(selectCustomerApiEndpoint)),
      switchMap(([action, endpoint]) =>
        this.http.post(`${endpoint}/tags`, action.payload)
      ),
      catchError(
        (err) => this.toastr.warning(`Failed to create tag.`) && throwError(err)
      ),
      tap(() => this.toastr.success("Tag created successfully.")),
      map((resp: Tag) => ({ type: createTagsSuccess.type, payload: resp }))
      // map((resp) => ({ type: fetchTags.type }))
    )
  );

  updateTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTag),
      withLatestFrom(this.store$.select(selectCustomerApiEndpoint)),
      switchMap(([action, endpoint]) =>
        this.http.put(`${endpoint}/tags/${action.payload.id}`, action.payload)
      ),
      catchError(
        (err) => this.toastr.warning(`Failed to update tag.`) && throwError(err)
      ),
      tap(() => this.toastr.success("Tag updated successfully.")),
      map((resp: Tag) => ({ type: updateTagSuccess.type, payload: resp }))

      // map((resp) => ({ type: fetchTags.type }))
    )
  );

  deleteTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTag),
      withLatestFrom(this.store$.select(selectCustomerApiEndpoint)),
      switchMap(([action, endpoint]) =>
        this.http.delete(`${endpoint}/tags/${action.payload.id}`)
      ),
      catchError(
        (err) => this.toastr.warning("Failed to delete tag.") && throwError(err)
      ),
      tap(() => this.toastr.success("Tag deleted successfully.")),
      map(() => ({ type: fetchTags.type }))
    )
  );

  fetchHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchHistory),
      withLatestFrom(this.store$.select(selectCustomerApiEndpoint)),
      switchMap(([action, endpoint]) => this.http.get(`${endpoint}/history`)),
      catchError(
        (err) =>
          this.toastr.warning(`Failed to fetch history`) && throwError(err)
      ),
      map((resp) => ({ type: fetchHistorySuccess.type, payload: resp }))
    )
  );

  // push
  sendTestPush$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendTestPush),
      withLatestFrom(
        this.store$.select(selectBaseEndpoint),
        this.store$.select(selectTestPushCredentials)
      ),
      switchMap(([action, endpoint, pushUser]) =>
        forkJoin([
          this.http.post(`${endpoint}/push-api/push/`, action.payload, {
            headers: {
              Authorization:
                "Basic " +
                window.btoa(pushUser.name + ":" + pushUser.clearTextPassword),
            },
            responseType: "text",
          }),
          of(action.payload),
        ])
      ),
      catchError(
        (err) =>
          this.toastr.warning("Failed to send test push.") && throwError(err)
      ),
      // tap(() => this.toastr.success('Successfully send Testpush.')),
      switchMap(([id, pushPayload]) => [
        { type: sendTestPushSuccess.type },
        { type: createQueueItem.type, payload: { id, pushPayload } },
      ])
    )
  );

  // status queue
  updateQueueTimer$ = createEffect(() =>
    timer(0, 1000).pipe(
      // ofType(sendTestPush),
      withLatestFrom(
        this.store$.select((state: AppState) =>
          state.queue.filter((q) => q.readyState < 100)
        )
      ),
      switchMap(([t, q]) =>
        q.map((e) => ({ type: fetchQueueStats.type, payload: e.id }))
      )
    )
  );

  fetchQueueStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchQueueStats),
      withLatestFrom(this.store$.select(selectCustomerApiEndpoint)),
      switchMap(([action, endpoint]) =>
        forkJoin([
          this.http.get<HistoryStatus[]>(
            `${endpoint}/stats/jobs/${action.payload}`
          ),
          of(action.payload),
        ])
      ),
      map(([resp, jobid]) => ({
        type: fetchQueueStatsSuccess.type,
        payload: { resp, jobid },
      }))
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      tap(async () => {
        await Auth.signOut();
        this.router.navigate([""]);
      }),
      map(() => ({ type: logoutSuccess.type }))
    )
  );

  appFormData(action): FormData {
    const formData = new FormData();
    formData.set(
      "data",
      new Blob([JSON.stringify(action.payload.app)], {
        type: "application/json",
      })
    );
    action.payload.files.forEach((file) => formData.set(file.key, file.binary));
    return formData;
  }

  constructor(
    private http: HttpClient,
    private actions$: Actions,
    private store$: Store<AppState>,
    private router: Router,
    private toastr: NbToastrService
  ) {}
}
